import React, { useEffect, useState } from "react";
import { Download, FileSignature } from "lucide-react";
import { toast } from "sonner";
import JeaCard from "@/components/jea/JeaCard";
import Skeleton from "@/components/jea/Skeleton";
import { demoClient, useDemo } from "@/pages/demo/DemoShell";
import { downloadPublicFile } from "@/lib/downloadFile";
import { formatMoney, formatDate } from "@/lib/pricing";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const DemoReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const demo = useDemo();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await demoClient.get("/demo/reports");
        setReports(data.reports || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const downloadReport = async (id) => {
    try { await downloadPublicFile(`${API_BASE}/demo/reports/${id}/pdf`); }
    catch { toast.error("Couldn't download PDF. Try again."); }
  };
  const downloadMemo = async (id) => {
    try { await downloadPublicFile(`${API_BASE}/demo/reports/${id}/attorney-memo`); }
    catch { toast.error("Couldn't download attorney memo. Try again."); }
  };

  return (
    <div className="space-y-6" data-testid="demo-reports-page">
      <div>
        <div className="jea-caption">Asset Discovery Output</div>
        <h1 className="text-[28px] font-bold text-white">Reports</h1>
        <p className="text-text-secondary text-[14px] mt-1">
          Every completed query becomes a full asset report + attorney memo. Download
          real branded PDFs of these demo reports — free, no sign-up required.
        </p>
      </div>

      <JeaCard className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-6"><Skeleton className="h-40" /></div>
        ) : reports.length === 0 ? (
          <div className="p-8 text-text-secondary text-center text-[13px]">No reports yet.</div>
        ) : (
          <table className="w-full" data-testid="demo-reports-table">
            <thead>
              <tr className="border-b border-border-default">
                {["Judgment", "Generated", "Brokers", "Cost", "Recovery %", "Download"].map((h) => (
                  <th key={h} className="jea-caption text-text-secondary p-4 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} className="border-b border-border-default hover:bg-bg-tertiary transition-colors">
                  <td className="p-4">
                    <button
                      onClick={() => demo?.openSignUp("viewing full reports")}
                      className="text-white font-semibold hover:text-accent-pink text-left"
                      data-testid={`demo-report-${r.id}`}
                    >
                      {r.debtor_name}
                    </button>
                  </td>
                  <td className="p-4 jea-date">{formatDate(r.generated_at)}</td>
                  <td className="p-4 text-text-secondary">{r.broker_count} brokers</td>
                  <td className="p-4 jea-mono text-accent-pink">{formatMoney(r.total_cost_cents)}</td>
                  <td className="p-4 text-white font-bold">{r.recovery_probability_pct ?? 0}%</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => downloadReport(r.id)}
                        className="inline-flex items-center gap-1 text-accent-pink hover:text-accent-pink-light text-[12px] font-semibold"
                        title="Download Report PDF"
                        data-testid={`demo-report-pdf-${r.id}`}
                      >
                        <Download size={12} /> PDF
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadMemo(r.id)}
                        className="inline-flex items-center gap-1 text-text-secondary hover:text-accent-pink text-[12px] font-semibold"
                        title="Download Attorney Memo"
                        data-testid={`demo-report-memo-${r.id}`}
                      >
                        <FileSignature size={12} /> Memo
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </JeaCard>
    </div>
  );
};

export default DemoReportsPage;
